/**
 * fields — the calculator's input layout as data. Grouped exactly as Noreen's workbook groups them
 * (site & charger / throughput / capital / operating / revenue / 10-year assumptions), so the form
 * is a map over this config rather than 24 hand-placed inputs. `benchmark` names the Read Me
 * BENCHMARK_RANGES row to surface beside the input; `pct` marks a field stored as a decimal but
 * edited as a percent (discount/growth/escalation).
 */
import type { RoiInputs } from '../../lib/roi/roiModel'
import type { ChargerType } from '../../lib/roi/presets'

/**
 * A spinner increment, optionally per charger class (T-506).
 *
 * `ratedPowerKw` is the reason this can vary: DC fast hardware is specified in ~25 kW steps
 * (50/100/150/…/350) while Level 2 is specified to a tenth (6.2/7.2/9.6/11.5/19.2). One static step
 * cannot serve both — 25 makes L2 unusable, 0.1 makes DCFC a hundred clicks. Resolved by stepFor().
 */
export type StepSpec = number | Record<ChargerType, number>

export interface FieldSpec {
  key: keyof RoiInputs
  label: string
  /** Suffix shown after the input, e.g. 'kW', '$', '$/kWh'. */
  unit?: string
  /**
   * Spinner increment. Omitted means `step="any"`, which still steps by **1** on an arrow click —
   * fine for a whole-dollar or whole-unit field, wrong for a money rate (T-496: the utility rate
   * jumped 0.18 → 1.18). Every `$/…` rate therefore sets an explicit step: the two price rates a
   * driver actually tunes (utility rate, price to driver) step 0.05 — a 0.01 cent-step was too fine
   * to adjust (T-529, Noreen xlsx items 11 & 12) — while the demand charge keeps 0.01. In every case
   * typing/pasting a finer value is unaffected, since step only constrains the spinner, not entry.
   *
   * T-506 extends the same defect class beyond money. A `%` field is STORED as a decimal and EDITED
   * as a percent, so the implicit 1 lands on the displayed number: cost escalation 2.5 became 3.5 in
   * one click — a 40% change to the assumption. Those step 0.25, a quarter point, which is how rates
   * are quoted. `ratedPowerKw` needs a step per charger class instead of one number; see StepSpec.
   */
  step?: StepSpec
  /** Stored as a decimal (0.08), edited/shown as a percent (8). */
  pct?: boolean
  /** BENCHMARK_RANGES.metric to surface as a directional hint next to this input. */
  benchmark?: string
}

/**
 * The spinner increment for this field and charger class.
 *
 * Defaults to 'any' — which browsers still step by 1 — for fields where a whole unit IS the natural
 * increment (ports, whole-dollar amounts). Only fields that declare a step opt out of that.
 */
export function stepFor(spec: FieldSpec, chargerType: ChargerType): number | 'any' {
  if (spec.step === undefined) return 'any'
  return typeof spec.step === 'number' ? spec.step : spec.step[chargerType]
}

export interface FieldGroup {
  title: string
  fields: FieldSpec[]
}

export const FIELD_GROUPS: FieldGroup[] = [
  {
    title: 'Site & charger',
    fields: [
      { key: 'ports', label: 'Ports', step: 1 },
      { key: 'ratedPowerKw', label: 'Rated power / port', unit: 'kW', step: { dcfc: 25, l2: 0.1 } },
      { key: 'operatingDays', label: 'Operating days / year', step: 1 },
    ],
  },
  {
    title: 'Throughput',
    fields: [
      {
        key: 'kwhPerPortPerDay',
        label: 'Energy / port / day',
        unit: 'kWh',
        benchmark: 'Energy dispensed / port / day',
      },
      { key: 'avgKwhPerSession', label: 'Avg kWh / session', unit: 'kWh' },
    ],
  },
  {
    title: 'Capital cost (one-time)',
    fields: [
      {
        key: 'hardwarePerPort',
        label: 'Hardware / port',
        unit: '$',
        benchmark: 'Hardware only per port',
      },
      { key: 'makeReadyPerPort', label: 'Make-ready + install / port', unit: '$' },
      { key: 'electricalUpgradesSite', label: 'Electrical / utility upgrades', unit: '$' },
      { key: 'permittingSoftCostsSite', label: 'Permitting + soft costs', unit: '$' },
      { key: 'contingencySite', label: 'Contingency', unit: '$' },
      { key: 'incentives', label: 'Incentives / rebates', unit: '$' },
    ],
  },
  {
    title: 'Operating cost (annual)',
    fields: [
      { key: 'utilityRatePerKwh', label: 'Utility rate', unit: '$/kWh', step: 0.05 },
      { key: 'demandChargePerKwMonth', label: 'Demand charge', unit: '$/kW-mo', step: 0.01 },
      { key: 'billedPeakKw', label: 'Billed peak demand', unit: 'kW' },
      { key: 'networkPerPortYr', label: 'Network / port / yr', unit: '$' },
      { key: 'maintenancePerPortYr', label: 'Maintenance / port / yr', unit: '$' },
      { key: 'insuranceFixedSiteYr', label: 'Insurance + fixed / yr', unit: '$' },
      { key: 'landLeaseSiteYr', label: 'Land lease / yr', unit: '$' },
    ],
  },
  {
    title: 'Revenue (annual)',
    fields: [
      {
        key: 'priceToDriverPerKwh',
        label: 'Price to driver',
        unit: '$/kWh',
        step: 0.05,
        benchmark: 'Price to driver ($/kWh)',
      },
      { key: 'idleFeeRevenueYr', label: 'Idle-fee revenue / yr', unit: '$' },
      { key: 'otherRevenueYr', label: 'Other revenue / yr', unit: '$' },
    ],
  },
  {
    title: '10-year assumptions',
    fields: [
      { key: 'discountRate', label: 'Discount rate', unit: '%', pct: true, step: 0.25 },
      { key: 'revenueGrowth', label: 'Revenue growth', unit: '%', pct: true, step: 0.25 },
      { key: 'costEscalation', label: 'Cost escalation', unit: '%', pct: true, step: 0.25 },
    ],
  },
]
